import { useState, useEffect, useCallback } from "react";
import { getVendorFinancialSummary } from "../../api/adminApi"; 
import {
  Box, Card, CardContent, Typography, Select, MenuItem, TextField, Button,
  CircularProgress, Stack, Grid, Chip 
} from "@mui/material";

import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

export default function VendorOrders({ vendorId }) {
  const [filter, setFilter] = useState("today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (filter === "custom" && (!startDate || !endDate)) return;
    try {
      setLoading(true);
      const params = { filter };
      if (filter === "custom") { params.start_date = startDate; params.end_date = endDate; }
      const res = await getVendorFinancialSummary(vendorId, params);
      setSummary(res.data);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || "Failed to load summary");
    } finally {
      setLoading(false);
    }
  }, [vendorId, filter, startDate, endDate]);

  useEffect(() => {
    if (filter !== "custom") fetchSummary();
  }, [filter, fetchSummary]);

  const handleExportExcel = () => {
    if (!summary || !summary.orders || summary.orders.length === 0) {
      alert("No data available to export");
      return;
    }
    let csvContent = "Order ID,Date,Total Amount (Rs),Items Ordered\n";
    summary.orders.forEach(order => {
      const date = new Date(order.created_at).toLocaleString();
      const itemsStr = order.items.map(i => `${i.name} (x${i.qty})`).join("; ");
      const safeDate = `"${date}"`;
      const safeItems = `"${itemsStr.replace(/"/g, '""')}"`;
      csvContent += `${order.order_id},${safeDate},${order.amount},${safeItems}\n`;
    });
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Vendor_${vendorId}_Orders_${filter}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const glassCardStyle = {
    borderRadius: '24px', border: "1px solid #ECE5DA", background: "linear-gradient(135deg, #FFFFFF 0%, #FFFDF8 100%)",
    boxShadow: '0 16px 40px rgba(58, 44, 35, 0.08)', position: 'relative', overflow: 'hidden'
  };

  return (
    <Box>
      <Card elevation={0} sx={{ borderRadius: '20px', border: '1px solid #ECE5DA', mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#A59D96', textTransform: 'uppercase', mb: 2 }}>Filter Order History</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Select value={filter} onChange={(e) => setFilter(e.target.value)} fullWidth sx={{ borderRadius: '12px', bgcolor: '#FFFDF8', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ECE5DA' } }}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
              <MenuItem value="custom">Custom Date Range</MenuItem>
            </Select>

            {filter === "custom" && (
              <>
                <TextField type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                <TextField type="date" label="End Date" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                <Button variant="contained" onClick={fetchSummary} disabled={!startDate || !endDate || loading} sx={{ borderRadius: '12px', py: 1.8, px: 4, bgcolor: '#3A2C23', color: 'white', fontWeight: 'bold', minWidth: '120px', '&:hover': { bgcolor: '#2F2A25' } }}>Search</Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#F8C332' }} /></Box>
      ) : summary ? (
        <>
          <Card elevation={0} sx={glassCardStyle}>
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(248, 195, 50, 0.1)', filter: 'blur(40px)' }} />
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <AssessmentIcon sx={{ color: '#F8C332', fontSize: 32 }} />
                  <Typography variant="h5" fontWeight="900" sx={{ color: '#3A2C23' }}>Order Analytics</Typography>
                </Box>
                <Chip icon={<CalendarMonthIcon fontSize="small" />} label={filter.toUpperCase()} sx={{ bgcolor: '#FFF6DE', color: '#D97706', fontWeight: '800', borderRadius: '10px' }} />
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 3, borderRadius: '20px', bgcolor: 'rgba(58, 44, 35, 0.03)', border: '1px solid rgba(58, 44, 35, 0.05)', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight="800" sx={{ color: '#A59D96', mb: 1, textTransform: 'uppercase' }}>Total Orders</Typography>
                    <Typography variant="h3" fontWeight="900" sx={{ color: '#3A2C23' }}>{summary.total_orders}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 3, borderRadius: '20px', bgcolor: 'rgba(52, 168, 83, 0.05)', border: '1px solid rgba(52, 168, 83, 0.1)', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight="800" sx={{ color: '#34A853', mb: 1, textTransform: 'uppercase' }}>Items Sold</Typography>
                    <Typography variant="h3" fontWeight="900" sx={{ color: '#2E7D32' }}>{summary.items_sold}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 3, borderRadius: '20px', bgcolor: 'rgba(248, 195, 50, 0.1)', border: '1px solid rgba(248, 195, 50, 0.2)', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight="800" sx={{ color: '#D97706', mb: 1, textTransform: 'uppercase' }}>Vendor Revenue</Typography>
                    <Typography variant="h4" fontWeight="900" sx={{ color: '#B45309', mt: 1 }}>₹{summary.total_amount}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Button
            fullWidth variant="contained" size="large" startIcon={<DownloadIcon />} onClick={handleExportExcel} disabled={summary.total_orders === 0}
            sx={{ mt: 3, py: 1.8, borderRadius: '16px', bgcolor: '#F8C332', color: '#3A2C23', fontWeight: '900', fontSize: '1.1rem', textTransform: 'none', boxShadow: '0 8px 24px rgba(248, 195, 50, 0.3)', '&:hover': { bgcolor: '#F5B71D', transform: 'translateY(-2px)' }, transition: 'all 0.2s ease', '&.Mui-disabled': { bgcolor: '#ECE5DA', color: '#A59D96' } }}
          >
            {summary.total_orders === 0 ? "No Data to Download" : "Download Excel Report"}
          </Button>
        </>
      ) : (
        <Typography textAlign="center" color="text.secondary" mt={5}>Select a filter to view analytics.</Typography>
      )}
    </Box>
  );
}